import React, { useState } from 'react';
import { Badge, Button, Icon, Table } from '@syneto/compass-react';
import moment from 'moment';
import Preloader from '../../../../components/preloader';
import {
	APPROVAL_STATUS_FILTER,
	COMPONENT_TYPES_PAGE_SIZE,
	COMPONENT_TYPES_SIZE_PER_PAGE_LIST,
	NOT_AVAILABLE
} from '../../../../constants/hardware-inventory';
import { approvedVersionOf } from '../../../../helpers/hardwareInventory';
import { filterByApprovalStatus, hasNoApprovedVersion, latestRefreshedAt } from './hardwareInventoryMapper';

const HardwareComponentTypesTable = (props) => {
	const {
		componentTypes,
		isLoading,
		hasError,
		isSaving,
		canApprove,
		onSelectComponentType,
		onApproveComponentType
	} = props;

	/*
	 * The whole catalog, unfiltered. There was a "Fragmented / All types" switch here that defaulted
	 * to types whose fleet spans more than one firmware version; it was removed because that
	 * predicate is true of very nearly every row - a fleet assembled over fifteen years with no
	 * update mechanism is split by construction, which is the premise of this feature rather than an
	 * anomaly within it - and false mostly for types deployed on a single appliance, where oldest and
	 * newest are the same reading. A control that is on for almost everything, and off for a sample
	 * size, sorts nothing.
	 *
	 * The oldest/newest columns still carry the spread as reference data, which is what a curator
	 * scanning versions actually reads (BRD REQ-CUR-04).
	 *
	 * The approval filter below is a different kind of control and defaults to showing everything:
	 * it narrows by whether a decision has been recorded, which is a fact about HITS rather than a
	 * guess about the fleet.
	 */
	const [approvalStatus, setApprovalStatus] = useState(APPROVAL_STATUS_FILTER.ALL);

	const visibleComponentTypes = filterByApprovalStatus(componentTypes, approvalStatus);
	const refreshedAt = latestRefreshedAt(componentTypes);
	const totalTypes = (componentTypes ?? []).length;
	const approvedTypes = filterByApprovalStatus(componentTypes, APPROVAL_STATUS_FILTER.APPROVED).length;

	const renderType = (cell, row) => {
		const derivedName = `${row.manufacturer ?? ''} ${row.model ?? ''}`.trim();

		return row.displayName ?? (derivedName || NOT_AVAILABLE);
	};

	const renderFirmwareVersion = (cell) => {
		return cell ?? NOT_AVAILABLE;
	};

	/*
	 * An approved row gets a check rather than an empty cell, so a blank Status cannot be misread as
	 * a row whose status failed to load. The icon carries its own accessible name, because a bare
	 * glyph says nothing to a screen reader.
	 */
	const renderAttention = (cell, row) => {
		if (hasNoApprovedVersion(row)) {
			return <Badge appearance="secondary">Not approved</Badge>;
		}

		return (
			<Badge appearance="success">
				<Icon name="fa fa-check" role="img" aria-label="Approved" title="Approved" />
			</Badge>
		);
	};

	/*
	 * Version and attribution in one column rather than three: `approvedBy` and `approvedAt` are only
	 * ever read together with the version they attribute, and two more columns push this table past
	 * what fits without a horizontal scrollbar.
	 *
	 * The attribution line is omitted rather than filled with N/A when the fields are absent, because
	 * absent is the expected state twice over - a type nobody has approved has no approver, and
	 * `fleet_component_firmware_analytics` is specified to copy only the version off the component
	 * type, so the two attribution fields may not reach this endpoint at all (see SYN-2800).
	 */
	const renderApproved = (cell, row) => {
		const approvedVersion = approvedVersionOf(row);

		if (!approvedVersion) {
			return NOT_AVAILABLE;
		}

		const attribution = [
			row.approvedBy,
			row.approvedAt ? moment(row.approvedAt).format('DD/MM/YYYY') : null
		].filter(Boolean);

		return (
			<>
				<div>{approvedVersion}</div>
				{attribution.length > 0 && <small className="text-muted">{attribution.join(' \u00b7 ')}</small>}
			</>
		);
	};

	/*
	 * "Approve" when nothing is recorded, "Change" when something is: the same endpoint overwrites a
	 * scalar either way, but a button that says Approve over a row already showing an approved
	 * version invites a second, duplicate decision.
	 */
	const renderApproveAction = (cell, row) => {
		return (
			<Button
				role="tertiary"
				disabled={!canApprove || isSaving}
				onClick={(event) => {
					/*
					 * The whole row is a link to the Fleet Inventory tab. Without stopping the event
					 * here, approving would also navigate away from the table it is acting on - and
					 * the modal would open over the wrong tab.
					 */
					event.stopPropagation();
					onApproveComponentType(row);
				}}
			>
				{hasNoApprovedVersion(row) ? 'Approve' : 'Change'}
			</Button>
		);
	};

	/*
	 * A failed fetch leaves nothing to tabulate, so an empty-catalog message would state a result the
	 * request never returned. The alert above carries the reason; here we only decline to draw a
	 * conclusion.
	 */
	const emptyTableState = () => {
		if (isLoading) {
			return <Preloader dataIsLoading={true} />;
		}

		if (hasError) {
			return 'Firmware analytics could not be loaded.';
		}

		/*
		 * Separated so an empty table says which of the two happened. A catalog that loaded and was
		 * then filtered to nothing is a different situation from one that has no rows at all, and
		 * during dark launch the first is the one an operator will hit by picking "Approved".
		 */
		if (totalTypes > 0) {
			return 'No component type matches this approval status.';
		}

		return 'No component types have firmware analytics yet.';
	};

	return (
		<div>
			<div className="d-flex justify-content-between align-items-center">
				<h6 className="mb-0">Firmware by component type</h6>

				<div className="d-flex align-items-center gap-3">
					{refreshedAt && (
						<span className="text-muted">
							Analytics refreshed {moment(refreshedAt).format('DD/MM/YYYY HH:mm')}
						</span>
					)}

					{/*
						The count is what keeps an empty "Approved" view legible. Nothing is approved until
						someone records it, and nothing is recorded during dark launch, so for now the filter
						correctly returns no rows - which without a stated "0 of 143" reads as a failed load.
						Dropped when nothing loaded at all, so "0 of 0" cannot pass for a surveyed catalog.
					*/}
					{!(hasError && totalTypes === 0) && (
						<span className="text-muted">
							{approvedTypes} of {totalTypes} approved
						</span>
					)}

					<select
						className="form-select"
						style={{ maxWidth: '170px' }}
						value={approvalStatus}
						onChange={(event) => { return setApprovalStatus(event.target.value); }}
					>
						<option value={APPROVAL_STATUS_FILTER.ALL}>All types</option>
						<option value={APPROVAL_STATUS_FILTER.APPROVED}>Approved</option>
						<option value={APPROVAL_STATUS_FILTER.NOT_APPROVED}>Not approved</option>
					</select>
				</div>
			</div>

			{/*
				The endpoint returns the whole catalog in one response, so paging here is client-side -
				there is nothing to fetch. It exists to keep a long catalog navigable, and `showTotal`
				answers "how big is the catalog?", which is otherwise invisible.
			*/}
			<Table
				data={visibleComponentTypes}
				keyField="id"
				pagination={{
					sizePerPageList: COMPONENT_TYPES_SIZE_PER_PAGE_LIST,
					sizePerPage: COMPONENT_TYPES_PAGE_SIZE,
					showTotal: true
				}}
				rowEvents={{ onClick: (e, row) => { return onSelectComponentType(row); } }}
				noDataIndication={emptyTableState()}
			>
				<Table.Col field="displayName" align="left" renderCell={renderType}>Component type</Table.Col>
				<Table.Col field="category" align="left">Category</Table.Col>
				<Table.Col field="oldestFirmwareVersion" align="left" renderCell={renderFirmwareVersion}>
					Oldest in fleet
				</Table.Col>
				<Table.Col field="newestFirmwareVersion" align="left" renderCell={renderFirmwareVersion}>
					Newest in fleet
				</Table.Col>
				<Table.Col field="approvedFirmwareVersion" align="left" renderCell={renderApproved}>
					Approved
				</Table.Col>
				<Table.Col field="attention" align="left" renderCell={renderAttention}>Status</Table.Col>
				<Table.Col field="approve" align="right" renderCell={renderApproveAction}>Approval</Table.Col>
			</Table>

			<p className="text-muted">
				Select a component type to list the appliances running it on the Fleet Inventory tab.
			</p>
		</div>
	);
};

export default HardwareComponentTypesTable;
